/**
 * @fileoverview View mode content for SAP movement details modal
 */
import React from "react";
import { formatDate } from '../../../utils/formatters';
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { Box, Grid } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import { DmsInfoCard } from "../../DmsDetailModal/DmsInfoCard";
import { DmsInfoField } from "../../DmsDetailModal/DmsInfoField";

/**
 * Format datetime for display
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("pt-BR");
};

/**
 * View mode content for displaying SAP movement details
 */
export const ViewModeContent = ({ data }) => {
  const movement = data;
  
  if (!movement) {
    return null;
  }

  return (
    <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
      {/* Movement Information */}
      <DmsInfoCard icon={<LocalShippingIcon />} title={intl.get("MovementInfo").d("Movement Information")}>
        <Grid container spacing={2}>
          <DmsInfoField 
            label={intl.get("DocumentNumber").d("Document Number")} 
            value={`${movement.document_number}/${movement.document_year}`} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("MovementType").d("Movement Type")} 
            value={movement.movement_type_text || movement.movement_type} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("MovementText").d("Movement Text")} 
            value={movement.movement_text || "-"} 
            xs={12}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("Quantity").d("Quantity")} 
            value={movement.quantity ? `${movement.quantity.toLocaleString("pt-BR")} ${movement.unit}` : "0"} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("Plant").d("Plant")} 
            value={movement.plant || "-"} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("StorageLocation").d("Storage Location")} 
            value={movement.storage_location || "-"} 
            xs={6}
            sm={4}
          />
        </Grid>
      </DmsInfoCard>

      {/* Material and Batch Information */}
      <DmsInfoCard icon={<InventoryIcon />} title={intl.get("MaterialAndBatchInfo").d("Material and Batch Information")}>
        <Grid container spacing={2}>
          <DmsInfoField 
            label={intl.get("MaterialCode").d("Material Code")} 
            value={movement.batch?.part?.number || movement.material_code || "-"} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("MaterialDescription").d("Description")} 
            value={movement.batch?.part?.description || "-"} 
            xs={12}
            sm={8}
          />
          <DmsInfoField 
            label={intl.get("BatchNumber").d("Batch")} 
            value={movement.batch?.number || "-"} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("FabricationDate").d("Fabrication Date")} 
            value={formatDate(movement.batch?.fabrication)} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("ExpirationDate").d("Expiration Date")} 
            value={formatDate(movement.batch?.expiration)} 
            xs={6}
            sm={4}
          />
        </Grid>
      </DmsInfoCard>

      {/* Additional Information */}
      <DmsInfoCard icon={<InventoryIcon />} title={intl.get("AdditionalInfo").d("Additional Information")}>
        <Grid container spacing={2}>
          <DmsInfoField 
            label={intl.get("CreatedOn").d("Created On")} 
            value={formatDateTime(movement.created_on)} 
            xs={6}
            sm={4}
          />
          <DmsInfoField 
            label={intl.get("UpdatedOn").d("Updated On")} 
            value={formatDateTime(movement.updated_on)} 
            xs={6}
            sm={4}
          />
        </Grid>
      </DmsInfoCard>
    </Box>
  );
};

ViewModeContent.propTypes = {
  data: PropTypes.object.isRequired,
};

